import { isDefined } from "~/shared/common";
import { FormKind, IconKind, PageKind, PropertyKind } from "~/shared/enums";
import type { Form, FormField, FormFieldUpdate, FormUpdate, Property } from "~/shared/types";
import { isString } from "~/shared/typeUtils";
import { makeRandomColorHex } from "~/utils/color";
import { makeDuid, makeEmptyLexicalState, randomSample } from "~/utils/common";
import { formComparator, formFieldComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, setObjectMapAndSorted, updateInSorted } from "./utils";

export type State = {
  _duidsToForms: Map<string, Form>;
  _duidsToFormFields: Map<string, FormField>;
  _sortedFormDuids: string[];
  _sortedFormFieldDuids: string[];
};

export const state: State = {
  _duidsToForms: new Map(),
  _duidsToFormFields: new Map(),
  _sortedFormDuids: [],
  _sortedFormFieldDuids: [],
};

export type Getters = {
  formList: Form[];
  getFormByDuid: (duid: string) => Form | undefined;
  formFieldList: FormField[];
  getFormFieldByDuid: (duid: string) => FormField | undefined;
  getFormFieldRelatedToFormOrdered: (formDuid: string) => FormField[];
};

export type Actions = {
  createForm: (order: string, partialForm?: Partial<Form>, options?: { awaitBackend?: boolean }) => Promise<Form>;
  updateForm: (update: FormUpdate, options?: { awaitBackend?: boolean }) => Promise<Form | undefined>;
  deleteForm: (form: Form, options?: { awaitBackend?: boolean }) => Promise<void>;
  $setForms: (forms: Form[]) => void;
  $createOrUpdateForm: (form: Form) => void;
  $deleteForm: (form: Form) => void;
  createFormField: (
    form: Form,
    property: Property,
    order: string,
    options?: { awaitBackend?: boolean }
  ) => Promise<FormField>;
  updateFormField: (update: FormFieldUpdate, options?: { awaitBackend?: boolean }) => Promise<FormField | undefined>;
  deleteFormField: (formField: FormField, options?: { awaitBackend?: boolean }) => Promise<void>;
  $setFormFields: (formFields: FormField[]) => void;
  $createOrUpdateFormField: (formField: FormField) => void;
  $deleteFormField: (formField: FormField) => void;
  $cleanupFormFieldsDefaultsNoBackend: (
    entityDuids: string[],
    propertyDuids: string[],
    options?: { removeAllValues?: boolean }
  ) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  formList() {
    return this._sortedFormDuids.map((e) => this._duidsToForms.get(e)).filter(isDefined);
  },
  getFormByDuid() {
    return (duid) => this._duidsToForms.get(duid);
  },
  formFieldList() {
    return this._sortedFormFieldDuids.map((e) => this._duidsToFormFields.get(e)).filter(isDefined);
  },
  getFormFieldByDuid() {
    return (duid) => this._duidsToFormFields.get(duid);
  },
  getFormFieldRelatedToFormOrdered() {
    return (formDuid) => this.formFieldList.filter((e) => e.formDuid === formDuid);
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async createForm(order, partialForm, options = {}) {
    const { awaitBackend = false } = options;
    const appStore = this.$useAppStore();

    const form: Form = {
      pageKind: PageKind.FORM,
      duid: makeDuid(),
      kind: FormKind.FORM,
      hidden: true,
      public: false,
      order,
      title: "Form",
      description: "",
      iconKind: IconKind.NONE,
      iconNameOrEmoji: randomSample(appStore.pageIconOptions)[0].name,
      colorHex: makeRandomColorHex(),
      ...partialForm,
    };

    this.$createOrUpdateForm(form);

    const defaultFormFields: FormField[] = (
      [
        [this.defaultTitleProperty, null],
        [this.defaultDartboardProperty, this.defaultDartboard?.duid ?? null],
        [this.defaultKindProperty, this.defaultTaskKind.duid],
        [this.defaultStatusProperty, this.defaultDefaultUnstartedStatus.duid],
      ] satisfies [Property, string | null][]
    ).map(([property, defaultValue], i) => ({
      duid: makeDuid(),
      formDuid: form.duid,
      propertyDuid: property.duid,
      locked: true,
      order: String.fromCharCode(i + 2),
      hidden: property.kind !== PropertyKind.DEFAULT_TITLE,
      required: true,
      label: "",
      default: {
        value: defaultValue,
      },
    }));

    defaultFormFields.forEach((e) => this.$createOrUpdateFormField(e));

    const backendAction = this.$backend.form.create(form, defaultFormFields);
    if (awaitBackend) {
      await backendAction;
    }
    return form;
  },
  async updateForm(update, options = {}) {
    const { awaitBackend = false } = options;
    const form = this.getFormByDuid(update.duid);
    if (!form) {
      return undefined;
    }

    Object.assign(form, update);
    updateInSorted(this._duidsToForms, this._sortedFormDuids, form.duid, formComparator);

    const backendAction = this.$backend.form.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return form;
  },
  async deleteForm(form, options = {}) {
    const { awaitBackend = false } = options;

    this.$deleteForm(form);

    const backendAction = this.$backend.form.delete(form.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $setForms(forms) {
    setObjectMapAndSorted(
      this._duidsToForms,
      this._sortedFormDuids,
      forms.map((e) => ({ ...e, pageKind: PageKind.FORM, kind: FormKind.FORM }) satisfies Form),
      formComparator
    );
  },
  $createOrUpdateForm(form) {
    const currentForm = this.getFormByDuid(form.duid);

    // eslint-disable-next-line no-param-reassign
    form.pageKind = PageKind.FORM;
    // eslint-disable-next-line no-param-reassign
    form.kind = FormKind.FORM;

    if (!currentForm) {
      this._duidsToForms.set(form.duid, form);
    } else {
      Object.assign(currentForm, form);
    }
    updateInSorted(this._duidsToForms, this._sortedFormDuids, form.duid, formComparator);
  },
  $deleteForm(form) {
    this._duidsToForms.delete(form.duid);
    removeFromSorted(this._sortedFormDuids, form.duid);
  },
  async createFormField(form, property, order, options = {}) {
    const { awaitBackend = false } = options;
    const formField: FormField = {
      duid: makeDuid(),
      formDuid: form.duid,
      propertyDuid: property.duid,
      locked: false,
      order,
      hidden: false,
      required: true,
      label: "",
      default: { value: null },
    };

    switch (property.kind) {
      case PropertyKind.DEFAULT_DESCRIPTION: {
        formField.default = { value: makeEmptyLexicalState() };
        break;
      }
      case PropertyKind.DEFAULT_ATTACHMENTS: {
        formField.required = false;
        formField.default = { value: [] };
        break;
      }
      case PropertyKind.DEFAULT_ASSIGNEES:
      case PropertyKind.DEFAULT_REVIEWERS:
      case PropertyKind.DEFAULT_TAGS:
      case PropertyKind.MULTISELECT:
      case PropertyKind.USER: {
        formField.default = { value: [] };
        break;
      }
      default: {
        break;
      }
    }

    this.$createOrUpdateFormField(formField);

    const backendAction = this.$backend.formField.create(formField);
    if (awaitBackend) {
      await backendAction;
    }
    return formField;
  },
  async updateFormField(update, options = {}) {
    const { awaitBackend = false } = options;
    const formField = this.getFormFieldByDuid(update.duid);
    if (!formField) {
      return undefined;
    }

    Object.assign(formField, update);
    updateInSorted(this._duidsToFormFields, this._sortedFormFieldDuids, formField.duid, formFieldComparator);

    const backendAction = this.$backend.formField.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return formField;
  },
  async deleteFormField(formField, options = {}) {
    const { awaitBackend = false } = options;

    this.$deleteFormField(formField);

    const backendAction = this.$backend.formField.delete(formField.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $setFormFields(formFields) {
    setObjectMapAndSorted(this._duidsToFormFields, this._sortedFormFieldDuids, formFields, formFieldComparator);
  },
  $createOrUpdateFormField(formField) {
    const currentFormField = this.getFormFieldByDuid(formField.duid);

    if (!currentFormField) {
      this._duidsToFormFields.set(formField.duid, formField);
    } else {
      Object.assign(currentFormField, formField);
    }
    updateInSorted(this._duidsToFormFields, this._sortedFormFieldDuids, formField.duid, formFieldComparator);
  },
  $deleteFormField(formField) {
    this._duidsToFormFields.delete(formField.duid);
    removeFromSorted(this._sortedFormFieldDuids, formField.duid);
  },
  $cleanupFormFieldsDefaultsNoBackend(entityDuids, propertyDuids, { removeAllValues = false } = {}) {
    const entityDuidsSet = new Set(entityDuids);
    const propertyDuidsSet = new Set(propertyDuids);

    const formFieldUpdates = this.formFieldList
      .filter((formField) => propertyDuidsSet.has(formField.propertyDuid))
      .filter((formField) => formField.default && "value" in formField.default)
      .map((formField) => {
        const defaultValue = formField.default;
        if (!defaultValue || !("value" in defaultValue)) {
          return { duid: formField.duid, default: defaultValue };
        }
        const newDefault = { ...defaultValue };
        const { value } = defaultValue;

        if (removeAllValues) {
          newDefault.value = null;
        } else if (Array.isArray(value)) {
          const filteredValues = value.filter((v) => isString(v) && !entityDuidsSet.has(v)).filter(isString);
          newDefault.value = filteredValues.length > 0 ? filteredValues : null;
        } else if (isString(value) && entityDuidsSet.has(value)) {
          newDefault.value = null;
        }

        return { duid: formField.duid, default: newDefault };
      });

    formFieldUpdates.forEach((update) => {
      const formField = this.getFormFieldByDuid(update.duid);
      if (formField) {
        Object.assign(formField, update);
      }
    });
  },
};

export { actions, getters };
